<template>
    <div>
        <h1 class="pt-4 text-2xl font-bold tracking-tight text-white md:pt-6 md:text-3xl">{{ greeting }}</h1>

        <!-- Quick picks -->
        <div v-if="homepage.featured" class="mt-5 grid grid-cols-2 gap-2 xl:grid-cols-3">
            <Link v-for="song in homepage.featured" :key="song.id" :href="'/song/' + song.id"
                class="group flex h-14 items-center gap-2.5 overflow-hidden rounded-md bg-white/[0.06] pr-2 sm:gap-3 sm:pr-3 transition-colors hover:bg-white/[0.1] md:h-16">
                <img :src="song.cover || song.artist?.image" :alt="song.name" class="h-full aspect-square shrink-0 object-cover" />
                <span class="min-w-0 flex-1">
                    <span class="line-clamp-2 text-[0.8125rem] font-semibold leading-tight text-white sm:line-clamp-1 sm:text-sm">{{ song.name }}</span>
                    <span class="hidden truncate text-xs text-nomad-400 sm:block">{{ song.artist?.name }}</span>
                </span>
                <button type="button" @click.prevent.stop="play(song)" :aria-label="'Play ' + song.name"
                    class="nl-play-fab hidden h-9 w-9 shrink-0 text-sm opacity-0 shadow-lift sm:inline-flex transition-opacity group-hover:opacity-100 focus:opacity-100">
                    <span class="icon-play translate-x-px" aria-hidden="true"></span>
                </button>
            </Link>
        </div>
        <div v-else class="mt-5 grid grid-cols-2 gap-2 xl:grid-cols-3">
            <div v-for="i in 6" :key="i" class="h-14 animate-pulse rounded-md bg-white/[0.05] md:h-16"></div>
        </div>

        <Shelf title="Trending" subtitle="What people are playing today" :items="trending">
            <template #default="{ item }">
                <MediaCard :href="'/song/' + item.id" :image="item.cover || item.artist?.image" :title="item.name"
                    :subtitle="item.artist?.name" :play="{ id: item.id, type: 'SONG', name: item.name }" />
            </template>
        </Shelf>

        <AdSlot placement="homeFeed" class="mt-10" />

        <Shelf title="Playlists" href="/playlists" :items="homepage.playlists">
            <template #default="{ item }">
                <MediaCard :href="'/playlist/' + item.id" :image="item.cover" :title="item.name"
                    :subtitle="item.songs_count + ' songs'" />
            </template>
        </Shelf>

        <div class="mt-10 grid grid-cols-1 gap-x-10 gap-y-10 xl:grid-cols-2">
            <section v-if="topWeek.length">
                <h2 class="nl-section-title mb-3 px-0.5">Top this week</h2>
                <TrackList :songs="topWeek" />
            </section>
            <section v-if="topArtists.length">
                <h2 class="nl-section-title mb-3 px-0.5">Top artists</h2>
                <ol>
                    <li v-for="(artist, index) in topArtists" :key="artist.id">
                        <Link :href="'/artist/' + artist.id"
                            class="group flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-white/[0.05] sm:gap-4 sm:px-3">
                            <span class="w-6 shrink-0 text-center text-sm tabular-nums text-nomad-400 sm:w-8">{{ index + 1 }}</span>
                            <img :src="artist.image" :alt="artist.name" loading="lazy" class="h-10 w-10 shrink-0 rounded-full object-cover" />
                            <span class="min-w-0 flex-1">
                                <span class="block truncate text-[0.9375rem] text-white group-hover:underline">{{ artist.name }}</span>
                                <span class="block text-sm text-nomad-400">
                                    {{ artist.songs_count !== undefined ? artist.songs_count + (artist.songs_count === 1 ? ' song' : ' songs') : 'Artist' }}
                                </span>
                            </span>
                        </Link>
                    </li>
                </ol>
            </section>
        </div>

        <AdSlot placement="homeFeed" class="mt-10" />

        <Shelf title="New episodes" href="/podcasts" :items="homepage.episodes">
            <template #default="{ item }">
                <MediaCard :href="'/episode/' + item.id" :image="item.image || item.podcast?.image" :title="item.title"
                    :subtitle="item.podcast?.title" :play="{ id: item.id, type: 'EPISODE', title: item.title }" />
            </template>
        </Shelf>

        <Shelf title="Events" :items="homepage.events" item-class="w-[55vw] max-w-[14rem] sm:w-52 xl:w-56">
            <template #default="{ item }">
                <MediaCard :href="'/event/' + item.id" :image="item.poster" :title="item.name" :subtitle="item.city" poster />
            </template>
        </Shelf>

        <Shorts />
    </div>
</template>
<script>
import AdSlot from "@/components/ui/AdSlot.vue";
import { defineAsyncComponent } from 'vue'
import { Link } from "@inertiajs/vue3";
import { mapState, mapActions, } from "vuex";
import Shelf from "@/components/ui/Shelf.vue";
import MediaCard from "@/components/ui/MediaCard.vue";
import TrackList from "@/components/ui/TrackList.vue";

export default {
    provide() {
        return {
            stream: this.$root.$data,
        };
    },
    data() {
        return {
            greeting: 'Welcome to Heesify',
        };
    },
    beforeMount() {
        document.title = 'Heesify - Somali Music Lyrics & More';
    },
    mounted() {
        // Set on the client only, so server and client render the same markup
        const hour = new Date().getHours();
        this.greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
        this.prepareHomepage();
    },
    computed: {
        ...mapState({
            homepage: (state) => state.homepage.all,
        }),
        trending() {
            return (this.homepage.trending || []).map((row) => row.song).filter(Boolean);
        },
        topWeek() {
            return (this.homepage.currentWeek || []).map((row) => row.song).filter(Boolean);
        },
        topArtists() {
            if (this.homepage.trendingArtists && this.homepage.trendingArtists.length) {
                return this.homepage.trendingArtists.slice(0, 6);
            }
            // trendingArtists can come back empty; fall back to the artists behind the top songs
            const seen = new Set();
            return [...this.trending, ...this.topWeek]
                .map((song) => song.artist)
                .filter((artist) => artist && !seen.has(artist.id) && seen.add(artist.id))
                .slice(0, 6);
        },
    },
    methods: {
        ...mapActions("homepage", ["prepareHomepage"]),
        play(song) {
            this.$store.dispatch('current/prepareCurrent', { id: song.id, type: 'SONG', name: song.name });
        },
    },
    components: {
        AdSlot,
        Link,
        Shelf,
        MediaCard,
        TrackList,
        Shorts: defineAsyncComponent(() => import('@/components/Shorts')),
    },
};
</script>
